/**
 * Fast Campaigns List Page
 * Shows all fast campaigns with status, links, and actions
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { copyToClipboard } from '@/lib/utils';
import { 
  Plus, 
  Zap, 
  ExternalLink, 
  Copy, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  Trash2,
  ArrowRight,
  Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FastCampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/api/fast-campaigns`);
      setCampaigns(response.data || []);
    } catch (error) {
      console.error('Error fetching fast campaigns:', error);
      toast.error('Failed to load fast campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text, id) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopied(id);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(null), 2000);
    }
  };

  const handleDelete = async (e, campaignId) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!window.confirm("Are you sure you want to delete this fast campaign?")) return;
    
    try {
      await axios.delete(`${API}/api/fast-campaigns/${campaignId}`);
      setCampaigns(campaigns.filter(c => c.id !== campaignId));
      toast.success("Fast campaign deleted");
    } catch (error) {
      toast.error("Failed to delete fast campaign");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-600 border border-green-500/20">
            <CheckCircle2 className="w-3 h-3" />
            Active
          </span>
        );
      case 'generating':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-600 border border-blue-500/20">
            <Loader2 className="w-3 h-3 animate-spin" />
            Generating
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-600 border border-red-500/20">
            <AlertCircle className="w-3 h-3" />
            Error
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-500/10 text-gray-600 border border-gray-500/20">
            <Clock className="w-3 h-3" />
            Draft
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/dashboard')}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Dashboard
              </button>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                <h1 className="text-xl font-semibold text-foreground">Fast Campaigns</h1>
              </div>
            </div>
            <Button 
              onClick={() => navigate('/fast-campaigns/new')}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="create-fast-campaign-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Fast Campaign
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Campaigns List */}
        {campaigns.length === 0 ? (
          <div className="text-center py-16">
            <Zap className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">No Fast Campaigns Yet</h2>
            <p className="text-muted-foreground mb-6">
              Create your first fast campaign to quickly personalize your website.
            </p>
            <Button 
              onClick={() => navigate('/fast-campaigns/new')}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Fast Campaign
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {campaigns.map((campaign) => (
              <div 
                key={campaign.id}
                className="group bg-surface border border-border rounded-xl p-5 hover:border-amber-500/50 transition-all cursor-pointer"
                onClick={() => navigate(`/fast-campaigns/${campaign.id}`)}
                data-testid={`fast-campaign-card-${campaign.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-foreground">{campaign.name}</h3>
                      {getStatusBadge(campaign.status)}
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-3">
                      Asset: {campaign.asset_name || 'Unknown'}
                    </p>
                    
                    {campaign.status === 'active' && campaign.magic_link && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(campaign.magic_link, `link-${campaign.id}`);
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
                        >
                          {copied === `link-${campaign.id}` ? (
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                          Copy Magic Link
                        </button>
                        <a
                          href={campaign.magic_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Preview
                        </a>
                      </div>
                    )}
                    
                    {campaign.status === 'draft' && (
                      <p className="text-xs text-amber-600">
                        Click to continue setup →
                      </p>
                    )}
                    
                    {campaign.status === 'error' && (
                      <p className="text-xs text-red-500">
                        {campaign.error_message || 'An error occurred'}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => handleDelete(e, campaign.id)}
                      className="p-1.5 hover:bg-destructive/10 rounded-lg transition-all opacity-40 hover:opacity-100"
                      title="Delete fast campaign"
                      data-testid={`delete-fast-campaign-${campaign.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </button>
                    <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
